import { Component, OnInit } from '@angular/core';
import { FaqAccordion } from '../../core/models/learn/faq-accordion';
import { shariahComplianceFaqLd, ldToList } from '../../../assets/static/json-ld.js';
import { StorageService } from '../../core/services/storage.service';
import { Role } from '../../core/enum/role.enum';

@Component({
    selector: 'app-shariah-compliant-investment',
    templateUrl: './shariah-compliant-investment.component.html',
    styleUrls: ['./shariah-compliant-investment.component.scss'],
    standalone: false
})
export class ShariahCompliantInvestmentComponent implements OnInit {
  pageFaqs: FaqAccordion[] = ldToList(shariahComplianceFaqLd);
  role: Role;
  Role = Role;

  constructor(private storageService: StorageService) {}

  ngOnInit() {
    this.role = this.storageService.role;
  }

  handleStartApplication() {
    if (!this.role) {
      return '/sign-up/register/4';
    }

    if (this.role === Role.IssuerRep) {
      return '/raise/raise-fund-form';
    }
  }

  // Copy is i18n-keyed (shariahPage.*); rendered via the `translate` pipe in the template.
  principles = [
    {
      title: 'shariahPage.principleJusticeTitle',
      description: 'shariahPage.principleJusticeDesc',
      positives: ['shariahPage.tagFairness', 'shariahPage.tagTransparency', 'shariahPage.tagHonesty'],
      negatives: ['shariahPage.tagUnjust', 'shariahPage.tagExploitation', 'shariahPage.tagDeceit'],
      imgSrc: 'shariah-justice.svg',
    },
    {
      title: 'shariahPage.principleConsentTitle',
      description: 'shariahPage.principleConsentDesc',
      positives: ['shariahPage.tagFreeWill', 'shariahPage.tagMutualAgreement'],
      negatives: ['shariahPage.tagManipulation', 'shariahPage.tagCoercion'],
      imgSrc: 'shariah-consent.svg',
    },
    {
      title: 'shariahPage.principleHarmTitle',
      description: 'shariahPage.principleHarmDesc',
      positives: [
        'shariahPage.tagFairDistributions',
        'shariahPage.tagTransparency',
        'shariahPage.tagEthicalActivities',
      ],
      negatives: ['shariahPage.tagInterest', 'shariahPage.tagGambling', 'shariahPage.tagHarmfulActivities'],
      imgSrc: 'shariah-harm.svg',
    },
    {
      title: 'shariahPage.principleTransparencyTitle',
      description: 'shariahPage.principleTransparencyDesc',
      positives: ['shariahPage.tagClearTerms'],
      negatives: ['shariahPage.tagHiddenInformation'],
      imgSrc: 'shariah-transparency.svg',
    },
    {
      title: 'shariahPage.principleRightsTitle',
      description: 'shariahPage.principleRightsDesc',
      positives: [
        'shariahPage.tagDueDiligence',
        'shariahPage.tagRespectOwnership',
        'shariahPage.tagClearRecords',
      ],
      negatives: ['shariahPage.tagUncertainty', 'shariahPage.tagTakingWithoutConsent'],
      imgSrc: 'shariah-rights.svg',
    },
  ];

  businessScreening = [
    {
      percentage: '5%',
      activities: [
        'shariahPage.activityConventionalBanking',
        'shariahPage.activityConventionalInsurance',
        'shariahPage.activityGambling',
        'shariahPage.activityNonHalalFood',
        'shariahPage.activityOtherNonCompliant',
      ],
    },
    {
      percentage: '20%',
      activities: [
        'shariahPage.activityShareTrading',
        'shariahPage.activityStockbroking',
        'shariahPage.activityRentalNonCompliant',
        'shariahPage.activityOtherNonCompliant',
      ],
    },
  ];

  investmentGuidelines = [
    {
      question: 'shariahPage.guidelineSocialQuestion',
      description: 'shariahPage.guidelineSocialDesc',
      category: 'shariahPage.guidelineSocialCategory',
      impacts: [
        'shariahPage.impactEducation',
        'shariahPage.impactHealthcare',
        'shariahPage.impactRenewableEnergy',
        'shariahPage.impactTechnology',
      ],
      isPositive: true,
    },
    {
      question: 'shariahPage.guidelineAvoidQuestion',
      description: 'shariahPage.guidelineAvoidDesc',
      category: 'shariahPage.guidelineAvoidCategory',
      impacts: [
        'shariahPage.tagExploitation',
        'shariahPage.impactAnyUnethical',
        'shariahPage.tagHarmfulActivities',
        'shariahPage.tagUncertainty',
      ],
      isPositive: false,
    },
  ];

  benefits = [
    {
      title: 'shariahPage.benefitEthicalTitle',
      description: 'shariahPage.benefitEthicalDesc',
      imgSrc: 'shariah-consent.svg',
    },
    {
      title: 'shariahPage.benefitRiskTitle',
      description: 'shariahPage.benefitRiskDesc',
      imgSrc: 'shariah-risk.svg',
    },
    {
      title: 'shariahPage.benefitDiversificationTitle',
      description: 'shariahPage.benefitDiversificationDesc',
      imgSrc: 'shariah-diverse.svg',
    },
    {
      title: 'shariahPage.benefitAccountabilityTitle',
      description: 'shariahPage.benefitAccountabilityDesc',
      imgSrc: 'shariah-accountability.svg',
    },
  ];

  founderBenefits = [
    {
      title: 'shariahPage.founderAlignmentTitle',
      description: 'shariahPage.founderAlignmentDesc',
      imgSrc: 'shariah-alignment.svg',
    },
    {
      title: 'shariahPage.founderDifferentiationTitle',
      description: 'shariahPage.founderDifferentiationDesc',
      imgSrc: 'shariah-market.svg',
    },
  ];

  assurances = [
    {
      title: 'shariahPage.assuranceOversightTitle',
      description: 'shariahPage.assuranceOversightDesc',
      imgSrc: 'shariah-oversight.svg',
    },
    {
      title: 'shariahPage.assuranceRecognizedTitle',
      description: 'shariahPage.assuranceRecognizedDesc',
      imgSrc: 'shariah-recognized.svg',
    },
    {
      title: 'shariahPage.assuranceScreeningTitle',
      description: 'shariahPage.assuranceScreeningDesc',
      imgSrc: 'shariah-screening.svg',
    },
  ];
}
